import { FC, useEffect, useRef, useState } from "react";
import Layout from "@/components/layout";
import paymentImage from "@/assets/images/payment.webp";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import LoadingScreen from "@/components/loading-screen";
import { WebsocketErrorListener } from "@/services/transaction-socket-handler";
import { Receipt } from "@/types/receipt";
import { Routes } from "@/types/routes";
import { StillThereModal } from "@/components/still-there-modal";
import { useMutation, useQuery } from "@tanstack/react-query";
import useTransactionSocketStore from "@/stores/transaction-socket";
import VectorFloor from "@/assets/images/VectorFloor";
import VectorTable from "@/assets/images/VectorTable";
import useCashRegisterStore from "@/stores/cash-register";
import { RegisterModel } from "@/types/shop";
import { playSound, SoundOption } from "@/helpers/playSound";
import FailedPaymentScreen from "@/pages/checkout-page/FailedPaymentScreen";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectReceipt } from "@/store/receipt/selectors";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setCurrentReceipt, setPaid } from "@/store/receipt/slice";
import { selectReceiptRequestBody } from "@/store/cart/selectors";

const CheckoutPage: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const receipt = useAppSelector(selectReceipt);
  const receiptBody = useAppSelector(selectReceiptRequestBody);
  const navigate = useNavigate();
  const { current: currentRegister } = useCashRegisterStore();
  const { socketHandler: transactionSocketHandler } = useTransactionSocketStore();
  const [wsError, setWsError] = useState<boolean>(false);
  const [waitingForTerminalCancel, setWaitingForTerminalCancel] = useState<boolean>(false);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const model = currentRegister?.model;

  const websocketErrorListener: WebsocketErrorListener = () => {
    removeTimer();
    setWsError(true);
  };

  const getReceipt = async (): Promise<Receipt> => {
    if (!receiptBody) return Promise.reject();
    const { data } = !!receipt
      ? await api.receipts.update(receipt.id, receiptBody)
      : await api.receipts.create(receiptBody);
    return Promise.resolve(data.data);
  };

  const {
    mutate: mutateTransaction,
    isLoading: isLoadingTransaction,
    isError: isErrorTransaction,
  } = useMutation(["transaction"], api.receipts.pay, {
    onSuccess: (transactionResponse) => {
      if (transactionResponse.status === 200) {
        dispatch(setPaid());
        return navigate(Routes.SUCCESS);
      }
      if (transactionSocketHandler.isConnected()) return;
      transactionSocketHandler.openConnection(receipt!.id);
    },
  });

  const { isLoading: isLoadingReceipt, isError: isErrorReceipt } = useQuery(
    ["receipt"],
    getReceipt,
    {
      refetchOnMount: "always",
      onSuccess: async (receipt) => {
        dispatch(setCurrentReceipt(receipt));
        mutateTransaction(receipt.id);
      },
    }
  );

  const removeTimer = () => {
    setWaitingForTerminalCancel(false);
    interval.current && clearInterval(interval.current);
  };

  useEffect(() => {
    transactionSocketHandler.addWsErrorListener(websocketErrorListener);
    return () => {
      transactionSocketHandler.removeWsErrorListener(websocketErrorListener);
      removeTimer();
    };
  }, []);

  if (isLoadingReceipt || isLoadingTransaction || waitingForTerminalCancel)
    return <LoadingScreen />;
  if (isErrorReceipt || isErrorTransaction || wsError) {
    playSound(SoundOption.PaymentError);
    return <FailedPaymentScreen />;
  }

  return (
    <Layout childrenClassName={styles.contentBox}>
      <div className={styles.imageWrapper}>
        <img src={paymentImage} alt="Checkout" className={styles.image} />
      </div>
      <div className={styles.mainWrapper}>
        <h1 data-cy="checkout-page-title">{t("checkoutPage.followTerminal")}</h1>
        {!!model && model == RegisterModel.TABLE ? (
          <VectorTable className={styles.table} />
        ) : (
          <VectorFloor className={styles.floor} />
        )}
      </div>
      <StillThereModal secondsBeforeModal={90} />
    </Layout>
  );
};

export default CheckoutPage;
